import type {SwatchType} from '@packages/systems/core/utils/StyleTypes';
import {reportBugOnce} from '@packages/utilities/reporter';

import {find, reject, trim, flowRight as compose} from 'lodash';
import {generateGuid, isGuid, trimGuid} from '@packages/utilities/guid';
import {
  humanizeNamedColor,
  closestColorName,
  getHSVA,
} from '@packages/systems/core/utils/ColorUtils';
import {assoc, prop} from '@packages/utilities/fp/utils';
import {weakMemo} from '@packages/utilities/memo';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import {Plugins} from '@packages/systems/wfdl';

const SWATCH_PREFIX = 'swatch_';
const SWATCH_PLACEHOLDER_PREFIX = `@${SWATCH_PREFIX}`;
const SWATCH_PLACEHOLDER_REGEX = /@swatch_([a-f0-9]{8})/g;

export const SWATCH_GUID_WIDTH = 8;

// This base64 image provides the checked background to help indicate when a swatch
// has some transparency in the color.
// prettier-ignore
export const LIGHT_CHECKERBOARD_ICON_BACKGROUND_URL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAIAAACQkWg2AAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAACpJREFUeNpi3LFjBwM2oK2tjVWciYFEMKqBGMCCK7yvXr06Gkr00wAQYACiEgXLqZw3aAAAAABJRU5ErkJggg==';
// NOTE: there is a dark checkerboard image that can be used as a React Component/SVG:
// `import DarkCheckerboardIcon from 'icons/style-panel/transparent-checkerboard-dark';`

export const LIGHT_CHECKERBOARD_ICON_BACKGROUND_POSITION = `-1px -1px / 8px auto`;

// prettier-ignore
export const generateLightCheckeredBackgroundImage = (color: string) => `linear-gradient(${color}, ${color}), url('${LIGHT_CHECKERBOARD_ICON_BACKGROUND_URL}') ${LIGHT_CHECKERBOARD_ICON_BACKGROUND_POSITION}`;

/**
 * Generates a CSS variable name to use for a swatch id.
 *
 * @param  {String} id The swatch ID, e.g. 'a1b2c3d4'
 * @return {String}    A CSS compatible variable name, e.g. 'swatch_a1b2c3d4'
 */
export function swatchVariableName(id: string) {
  return `${SWATCH_PREFIX}${id}`;
}

/**
 * Generates a placeholder to use in styleLess values to refer to a swatch.
 *
 * @param  {String} id The swatch ID, e.g. 'a1b2c3d4'
 * @return {String}    A styleLess compatible placeholder, e.g. '@swatch_a1b2c3d4'
 */
export function swatchPlaceholder(id: string) {
  return `${SWATCH_PLACEHOLDER_PREFIX}${id}`;
}

/**
 * Generates an 8 character ID for a new swatch.
 *
 * @return {String}
 */
export function generateSwatchGuid() {
  return generateGuid(SWATCH_GUID_WIDTH);
}

/**
 * Detects whether a string exactly matches a swatch placeholder.
 *
 * @param  {String}  lessValue  A styleLess value.
 * @return {Boolean}            True if the string is a swatch placeholder, e.g. "@swatch_12345678"
 */
export function isSwatchPlaceholder(lessValue: string) {
  return /^@swatch_[a-f0-9]{8}$/.test(lessValue);
}

/**
 * Detects whether a string contains a @swatch_12345678 placeholder.
 *
 * @param  {String}  lessValue  A styleLess value.
 * @return {Boolean}            True if the string contains a swatch placeholder somewhere inside of it.
 */
export function hasSwatchPlaceholder(lessValue: string | null): boolean {
  return lessValue
    ? lessValue.split(/[ ,;()]/).some(isSwatchPlaceholder)
    : false;
}

export function getSwatchPlaceholders(
  lessValue?: string | null
): Array<string> {
  if (!lessValue) return [];
  const placeholders: Array<string> = [];
  for (const match of lessValue.matchAll(SWATCH_PLACEHOLDER_REGEX)) {
    const swatchId = match[1];
    if (swatchId) {
      placeholders.push(swatchId);
    }
  }
  return placeholders;
}

/**
 * Converts the CssVariablesStore.swatches to a simple guid->color map.
 *
 * @param  {Array} swatches
 * @return {Object}
 */
export const createSwatchMap = weakMemo(
  (
    swatches: Array<SwatchType>
  ): {
    [id: string]: string;
  } =>
    swatches.reduce<Record<string, any>>((memo, {id, color}) => {
      memo[id] = color;
      return memo;
    }, {})
);

/**
 * Attempt to render a legacy swatch as its migrated variable.
 *
 * If no such migrated variable exists, or if there's any other unexpected error
 * rendering the CSS variable name, returns `undefined`.
 */
const maybeRenderMigratedSwatchToCssVariable = (
  swatchId: string,
  variables: StyleVariables.VariableMap,
  variableCollections: StyleVariables.VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  mut_seenVariables?: Set<StyleVariables.VariableID>
): undefined | string => {
  if (variables.size === 0) return;

  const variableId = StyleVariables.convertStringToVariableId(
    `variable-${swatchId}`
  );

  // We first check that the variable exists; without this check we would always
  // render `[unknown]` CSS variable names in cases where the site has not been
  // migrated to using variables.
  const variable = variables.get(variableId);
  if (variable) {
    const cssVariableName = StyleVariables.renderCssVariableName(
      variables,
      variableCollections,
      slugMap,
      variable.id
    );
    mut_seenVariables?.add(variableId);

    return `var(--${cssVariableName})`;
  }
};

/**
 * Attempt to render a legacy swatch as its migrated variable, and resolve the
 * value to a primitive for use in XSCP.
 *
 * If no such migrated variable exists, or if there's any other unexpected error
 * rendering the primitive, returns `undefined`.
 */
const maybeRenderMigratedSwatchToBakedPrimitive = (
  swatchId: string,
  variables: StyleVariables.VariableMap
): undefined | string => {
  if (variables.size === 0) return;

  const variableId = StyleVariables.convertStringToVariableId(
    `variable-${swatchId}`
  );
  const variable = variables.get(variableId);

  if (variable) {
    const primitive = StyleVariables.resolvePreviewValue(variables, variableId);
    if (primitive instanceof Error) {
      reportBugOnce('UnexpectedVariableResolutionError', primitive.message, {
        team: 'dse',
        ticket: 'https://webflow.atlassian.net/browse/DSE-7060',
      });
      return;
    }

    // We don't expect to encounter raw values here
    if (primitive.type === 'raw') {
      reportBugOnce(
        'UnexpectedRawVariableValue',
        `maybeRenderMigratedSwatchToBakedPrimitive: Did not expect to encounter a raw value for swatch`,
        {
          team: 'styles',
          ticket: 'https://webflow.atlassian.net/browse/DSE-7060',
        }
      );
      return;
    }
    return StyleVariables.convertStylePrimitiveToCSSValue(primitive);
  }
};

/**
 * Replaces @swatch_12345678 values with CSS variable access syntax.
 *
 * @param  {String} lessValue A styleLess value, with 0 or more @swatch placeholders.
 * @return {String}           The same value, but with @swatch_ placeholders replaced with CSS variable lookup syntax.
 */
export function replaceSwatchPlaceholdersForCSSOM(
  lessValue: string,
  variables: StyleVariables.VariableMap,
  variableCollections: StyleVariables.VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap']
) {
  return lessValue.replace(SWATCH_PLACEHOLDER_REGEX, (fullMatch, swatchId) => {
    return (
      maybeRenderMigratedSwatchToCssVariable(
        swatchId,
        variables,
        variableCollections,
        slugMap
      ) ?? `var(--swatch_${swatchId})`
    );
  });
}

/**
 * Replaces @swatch_12345678 values with baked CSS colors.
 *
 * @param  {String} lessValue A value from styleLess, with 0 or more @swatch placeholders.
 * @param  {Map}    swatchMap A SwatchId => CSS Color map.
 * @return {String}           The styleLess value with @swatch_ placeholders replaced with the actual CSS color of each swatch.
 */
export function replaceSwatchPlaceholdersForExport(
  lessValue: string,
  swatchMap: {
    [id: string]: string;
  },
  variables: StyleVariables.VariableMap,
  variableCollections: StyleVariables.VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  mut_seenVariables?: Set<StyleVariables.VariableID>
) {
  return lessValue.replace(SWATCH_PLACEHOLDER_REGEX, (fullMatch, swatchId) => {
    return (
      maybeRenderMigratedSwatchToCssVariable(
        swatchId,
        variables,
        variableCollections,
        slugMap,
        mut_seenVariables
      ) ??
      (swatchMap[swatchId] || 'transparent')
    );
  });
}

export function replaceSwatchPlaceholdersForXSCP(
  lessValue: string,
  swatchMap: {
    [id: string]: string;
  },
  variables: StyleVariables.VariableMap
) {
  return lessValue.replace(SWATCH_PLACEHOLDER_REGEX, (fullMatch, swatchId) => {
    return (
      maybeRenderMigratedSwatchToBakedPrimitive(swatchId, variables) ??
      (swatchMap[swatchId] || 'transparent')
    );
  });
}

/**
 * Replaces @swatch_12345678 values with @var_variable-12345678 if variable exists, otherwise with @swatch_12345678.
 *
 * @param  {String} lessValue A styleLess value, with 0 or more @swatch placeholders.
 * @return {String}           The same value, but with @swatch_ placeholders replaced with CSS variable lookup syntax.
 */
export function replaceSwatchPlaceholdersWithVariables(
  lessValue: string,
  variableMap: Map<string, StyleVariables.AnyVariableType>
) {
  return lessValue.replace(SWATCH_PLACEHOLDER_REGEX, (_, swatchId) => {
    const variableId = `variable-${swatchId}`;

    if (variableMap.has(variableId)) {
      return `@var_${variableId}`;
    }

    return `@swatch_${swatchId}`;
  });
}

/**
 * Finds and returns the swatch object with the associated id
 *
 * @param  {List}   swatches  An array of swatch objects.
 * @param  {String} id        The id associated with the global swatch
 * @return {Object}           Swatch object with name, value, color, and id if found
 */
export function findSwatch(
  swatches: Array<SwatchType>,
  id: string
): SwatchType | undefined {
  return find(swatches, (swatch) => swatch.id === id);
}

export const getSwatchColor = compose(prop('color'), findSwatch);
export const getSwatchColorHSVA = compose(getHSVA, prop('color'), findSwatch);

/**
 * Extracts the id from a swatch placeholder
 *
 * @param  {String} placeholder The full swatch placeholder
 * @return {String}             id of swatch
 */
export function getSwatchIdFromPlaceholder(id: string) {
  if (hasSwatchPlaceholder(id)) {
    // @ts-expect-error - TS2531 - Object is possibly 'null'.
    return id.match(SWATCH_PLACEHOLDER_REGEX)[0].split('_')[1];
  }
}

/**
 * Creates a new swatch
 *
 * @param {String}  color    An object of swatches
 * @param {String}  name     Name of the new swatch
 * @param {String}  id       Unique ID of newSwatch
 * @return {Object}          Swatch object
 */
export function createNewSwatch(
  color: string,
  name: string,
  id: string
): SwatchType {
  return {
    name: trim(name) || humanizeNamedColor(closestColorName(color)),
    id,
    color,
    previewColor: '',
  };
}

/**
 * Removes an existing swatch and returns an array of remaining swatches
 *
 * @param  {Array}   swatches  An list of swatches
 * @param  {String}  swatchId  Id of the swatch to be removed
 * @return {Array}             New array of leftover swatches
 */
export function deleteSwatch(
  swatches: Array<SwatchType>,
  swatchId: string
): Array<SwatchType> {
  return reject(swatches, (swatch) => swatch.id === swatchId);
}

/**
 * Edits the color in a "transient" state, without persisting the color. This is in order
 * to show the user the color changing live in the canvas. Doing this also allow us to store the
 * previous color value that we could revert back to in case the user cancels.
 *
 * @param  {Array}  swatches      List of swatches
 * @param  {String} options.id    Id of swatch being edited
 * @param  {String} options.color Color to apply to swatch being edited.
 * @return {Array}  swatches      New list of swatches including the swatch that was edited.
 */
export function changeSwatchColor(
  swatches: Array<SwatchType>,
  {
    id,
    color,
  }: {
    id: string;
    color: string;
  }
): Array<SwatchType> {
  return swatches.map((swatch) =>
    swatch.id === id ? assoc('previewColor')(color)(swatch) : swatch
  );
}

/**
 * This helper saves the actual changes made to the swatch.
 *
 * @param  {Array}   swatches         List of existing swatches
 * @param  {String}  options.id       Id of the swatch being edited
 * @param  {String}  options.name     New name of the swatch being edited.
 * @return {Array}                    Returns new list of swatches that include the edited swatch
 */
export function saveSwatchEdit(
  swatches: Array<SwatchType>,
  {
    id,
    name,
  }: {
    id: string;
    name: string;
  }
): Array<SwatchType> {
  return swatches.map((swatch) =>
    swatch.id === id
      ? {
          name: name || swatch.name,
          color: swatch.previewColor || swatch.color,
          previewColor: '',
          id,
        }
      : swatch
  );
}

/**
 * Cancels the edit and resets preview color for all swatches
 *
 * @param  {Array} swatches  List of swatches
 * @param  {String} id       Id of swatch being edited to be cancelled
 * @return {Array}           New list of swatches
 */
export function cancelSwatchEdit(
  swatches: Array<SwatchType>
): Array<SwatchType> {
  return swatches.map((swatch) =>
    swatch.previewColor ? assoc('previewColor')('')(swatch) : swatch
  );
}

export type LegacySwatchType = SwatchType & {
  _guid?: string;
};
/**
 * Cleans up incoming injected swatches from the backend; swatches now store their IDs as .id, instead of ._guid
 * @param  {Array} swatches Incoming swatches
 * @return {Array} swatches Cleaned swatches
 */
export function cleanSwatches(
  swatches: Array<LegacySwatchType>
): Array<SwatchType> {
  return swatches.map((swatch) => {
    const {_guid = swatch.id || generateSwatchGuid(), name, color} = swatch; // ._guid is now set as .id
    const id = isGuid(_guid) ? trimGuid(_guid, SWATCH_GUID_WIDTH) : _guid; // Trim full guids to short ones

    // We want to trim previewColor from the backend: this indicates that the
    // swatch editor was closed without saving or canceling, and previewColor
    // was synced. previewColor should not exist except while editing swatch.
    // (https://github.com/webflow/webflow/issues/23720)
    return {id, name, color, previewColor: ''};
  });
}
